/**
 * Repeated failures are one incident (docs/plans/2026-10-hermes-adoption.md, item 3). AIOE's own
 * contract (the console and the phone read it; the workbench needs nothing: it already reports every
 * failed run).
 *
 * When a routine, a cloud agent, a node's run or a member's automated run fails with the same error as
 * its last failure, that is one incident, not one more row: a count, a first and last time, the error.
 * It is acknowledged once and stays quiet for the repeat interval; a different error is a different
 * incident. A person's own interactive run is never an incident.
 *
 *   GET  /projects/v1/:id/incidents?state=open|all          { incidents }
 *   POST /projects/v1/:id/incidents/:iid/acknowledge        → the incident
 *   POST /projects/v1/:id/incidents/:iid/close              → the incident
 */
import { z } from "zod";
export const INCIDENT_KINDS = ["routine", "cloud-agent", "node", "member"];
export const incidentKindSchema = z.enum(INCIDENT_KINDS);
export const INCIDENT_STATES = ["open", "acknowledged", "closed"];
export const incidentStateSchema = z.enum(INCIDENT_STATES);
/** An acknowledged incident stays quiet for this long; a repeat after it is raised again. */
export const INCIDENT_REPEAT_HOURS = 24;
export const incidentSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    kind: incidentKindSchema,
    /** The routine's id, the cloud run's member, the node's agent, the team member (`<teamId>/<agent>`). */
    subjectId: z.string(),
    subjectName: z.string(),
    /** SHA-256, hex, of the normalised error (digits, ids, paths and durations taken out). */
    signature: z.string(),
    /** The first 500 characters of the newest error. */
    error: z.string(),
    count: z.number().int().positive(),
    firstAt: z.string(),
    lastAt: z.string(),
    lastRunId: z.string(),
    state: incidentStateSchema,
    acknowledgedBy: z.string().optional(),
    acknowledgedAt: z.string().optional(),
    closedBy: z.string().optional(),
    closedAt: z.string().optional(),
    /** When the inbox record was last raised. */
    notifiedAt: z.string().optional(),
});
/** The inbox action that acknowledges an incident from its record ({ incidentId }); `…close` closes it. */
export const INCIDENT_ACKNOWLEDGE_RPC = "aioe.incident.acknowledge";
export const INCIDENT_CLOSE_RPC = "aioe.incident.close";
export const INCIDENT_KIND_TEXT = { routine: "Routine", "cloud-agent": "Cloud agent", node: "Node", member: "Automated member" };
export const INCIDENT_STATE_TEXT = { open: "Open", acknowledged: "Acknowledged", closed: "Closed" };
/**
 * The error with what varies between repeats taken out: hex and UUID-like ids, numbers, paths, durations,
 * quoted strings; whitespace collapsed; lower-cased. Two failures with the same normalised error are one incident.
 */
export function normaliseError(error) {
    return error
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<id>")
        .replace(/\b[0-9a-f]{12,}\b/gi, "<id>")
        .replace(/(?:[A-Za-z]:)?(?:[\\/][\w.@-]+){2,}/g, "<path>")
        .replace(/https?:\/\/\S+/gi, "<url>")
        .replace(/"[^"\n]*"|'[^'\n]*'|`[^`\n]*`/g, "<text>")
        .replace(/\b\d+(?:\.\d+)?\s*(?:ms|s|sec|seconds?|m|min|minutes?|h|hours?|kb|mb|gb|%)\b/gi, "<n>")
        .replace(/\d+/g, "<n>")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
        .slice(0, 1000);
}
/** The words of an incident's inbox record: "7 times since 09:14 · <error>". */
export function incidentBody(i, clock) {
    const times = i.count === 1 ? "Once" : `${i.count} times`;
    return `${times}${i.count === 1 ? ` at ${clock(i.firstAt)}` : ` since ${clock(i.firstAt)}`} · ${i.error}`.slice(0, 4000);
}
export const incidentTitle = (i) => (i.count === 1 ? `${i.subjectName} failed` : `${i.subjectName} keeps failing`).slice(0, 300);
//# sourceMappingURL=incidents.js.map